import { DotsThree, Plus, UsersThree } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCurrentWorkspace, useDeleteGroup, useGroups } from "~/api/queries";
import { ABILITY, type Group } from "~/api/types";
import { GroupDialog } from "~/features/peopleAdmin/GroupDialog";
import { GroupMembersDialog } from "~/features/peopleAdmin/GroupMembersDialog";
import { AnchorProjectDialog } from "~/features/projects/AnchorProjectDialog";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { usePolicy } from "~/policies/usePolicy";

function GroupRow({ group, canManage }: { group: Group; canManage: boolean }) {
  const { t } = useTranslation();
  const can = usePolicy(group.id);
  const deleteGroup = useDeleteGroup();
  const [managingMembers, setManagingMembers] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [anchoring, setAnchoring] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const items: MenuItem[] = [
    { type: "button", title: t("Members…"), onClick: () => setManagingMembers(true) },
    { type: "button", title: t("Rename…"), onClick: () => setRenaming(true) },
    { type: "button", title: t("Anchor to a project…"), onClick: () => setAnchoring(true) },
    { type: "separator" },
    { type: "button", title: t("Delete…"), dangerous: true, onClick: () => setConfirmDelete(true) },
  ];

  return (
    <li className="flex items-center gap-3 border-b border-divider px-3 py-2.5 last:border-b-0">
      <UsersThree size={18} aria-hidden className="shrink-0 text-text-tertiary" />
      <span className="min-w-0 flex-1 truncate text-body text-text">{group.name}</span>
      <span className="text-small text-text-tertiary">
        {t("{{count}} members", { count: group.memberCount })}
      </span>
      {can.update ? null : <span className="text-small text-text-tertiary">{t("Managed")}</span>}
      {canManage ? (
        <Menu
          items={items}
          trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} size="sm" />}
        />
      ) : null}

      {managingMembers ? (
        <GroupMembersDialog group={group} onClose={() => setManagingMembers(false)} />
      ) : null}
      {renaming ? <GroupDialog group={group} onClose={() => setRenaming(false)} /> : null}
      {anchoring ? <AnchorProjectDialog group={group} onClose={() => setAnchoring(false)} /> : null}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("Delete {{name}}?", { name: group.name })}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={deleteGroup.isPending}
        onConfirm={() =>
          deleteGroup.mutate(group.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              toast.success(t("Team deleted"));
            },
          })
        }
      />
    </li>
  );
}

export function Groups() {
  const { t } = useTranslation();
  const { data: groups, isLoading } = useGroups();
  const { data: workspace } = useCurrentWorkspace();
  const [creating, setCreating] = useState(false);

  const canManage = workspace?.abilities[ABILITY.workspaceManageMembers] === true;

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {canManage ? (
        <div className="flex justify-end">
          <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
            {t("New team")}
          </Button>
        </div>
      ) : null}

      {!groups?.length ? (
        <Empty icon={UsersThree} message={t("No groups yet")} />
      ) : (
        <ul className="flex flex-col rounded-md border border-divider">
          {groups.map((group) => (
            <GroupRow key={group.id} group={group} canManage={canManage} />
          ))}
        </ul>
      )}

      {creating ? <GroupDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}
